import { AnimatePresence, LayoutGroup, motion } from 'framer-motion';
import { ChangeEvent, KeyboardEvent, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
  ChatHeader,
  ChatMessageBubble,
  ChatThread,
  ChatThreadCard,
  CitationsSection,
  PdfContext,
  PdfPasswordModal,
  PdfPreviewDialog,
  StartingChatMessage,
  StartingChatMessageCard,
  TooltipLabel,
  UploadedFile,
  useAutoScroll,
  useAutosizeTextArea,
  useChatFileManager,
  useUploadPdf,
} from '@eluve/blocks';
import {
  Divider,
  HStack,
  Heading,
  Icon,
  Input,
  Link,
  NewButton,
  Text,
  Textarea,
  VStack,
  cn,
} from '@eluve/components';
import { useAnalytics } from '@eluve/posthog-react';
import { useUserIdFromSession } from '@eluve/session-helpers';

import { ChatMessageFeedback } from './ChatMessageFeedback';

// Types

type PasswordModalState = {
  isOpen: boolean;
  fileId: string | null;
  fileName: string | null;
};

type ChatProps = {
  currentThreadId: string | null;
  draftMessage: string;
  status?: 'submitted' | 'streaming' | 'ready' | 'error';
  onCreateThread: () => void | Promise<void>;
  onMessageSend: (content?: string) => void;
  onSelectThread: (threadId: string | null) => void;
  setDraftMessage: (message: string) => void;
  startingMessages?: StartingChatMessage[];
  threads: ChatThread[];
  onThreadTitleUpdate?: (threadId: string, newTitle: string) => void;
};

// Components

export const Chat = ({
  currentThreadId,
  draftMessage,
  status,
  onMessageSend,
  onCreateThread,
  onSelectThread,
  setDraftMessage,
  startingMessages,
  threads,
  onThreadTitleUpdate,
}: ChatProps) => {
  const { tenantId, patientId } = useParams() as {
    tenantId: string;
    patientId: string;
  };
  const userId = useUserIdFromSession();
  const analytics = useAnalytics('chat');
  const pdfManager = useChatFileManager({
    tenantId,
    patientId,
    currentThreadId,
  });

  const [passwordModal, setPasswordModal] = useState<PasswordModalState>({
    isOpen: false,
    fileId: null,
    fileName: null,
  });

  const handlePdfUploadComplete = (file: UploadedFile) => {
    pdfManager?.setUploadedFiles((prev) => [...prev, file]);

    analytics.capture('chat_thread_file_added', {
      tenantId,
      userId,
      patientId,
      threadId: currentThreadId,
      fileId: file.id,
      fileName: file.fileName,
      fileSize: file.fileSize,
    });

    // Show password modal if file has password
    if (file.hasPassword) {
      setPasswordModal({
        isOpen: true,
        fileId: file.id,
        fileName: file.fileName || null,
      });
    }
  };

  const fileInputRef = useRef<HTMLInputElement>(null);

  const { uploadFile, isUploading } = useUploadPdf({
    tenantId,
    threadId: currentThreadId ?? '',
    onUploadComplete: handlePdfUploadComplete,
  });

  const handleFileRemove = async (fileId: string) => {
    const fileToRemove = pdfManager?.uploadedFiles.find((f) => f.id === fileId);
    if (fileToRemove) {
      const { id: fileId, fileName, fileSize } = fileToRemove;
      analytics.capture('chat_thread_file_removed', {
        tenantId,
        userId,
        patientId,
        threadId: currentThreadId,
        fileId,
        fileName,
        fileSize,
      });
    }
    await pdfManager?.handleRemove(fileId);
  };

  const currentThread = threads?.find(
    (thread) => thread.id === currentThreadId,
  );
  const hasMessages =
    currentThread?.messages && currentThread?.messages.length > 0;
  const isStreaming = status === 'streaming';
  const isPending = status === 'submitted';
  const isLoading = isStreaming || isUploading;

  const messageListRef = useAutoScroll([currentThread?.messages, isStreaming], {
    disengageThresholdPx: Infinity,
  });

  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  useAutosizeTextArea({
    ref: textAreaRef,
    maxHeight: 120,
    dependencies: [draftMessage],
  });

  useEffect(() => {
    if (currentThreadId && !isLoading) {
      textAreaRef.current?.focus();
    }
  }, [currentThreadId, isLoading]);

  const handleDraftMessageChange = (
    event: ChangeEvent<HTMLTextAreaElement>,
  ) => {
    setDraftMessage(event.target.value);
  };

  const handleDraftMessageKeyDown = (
    event: KeyboardEvent<HTMLTextAreaElement>,
  ) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      textAreaRef.current?.blur();
      event.preventDefault();
      handleSendMessageClick();
    } else if (event.key === 'Escape') {
      textAreaRef.current?.blur();
      event.preventDefault();
    }
  };

  const handleSendMessageClick = () => {
    if (draftMessage.length === 0) {
      return;
    }
    onMessageSend();
    setDraftMessage('');
  };

  const handleStartingMessageSelect = (content: string) => {
    onMessageSend(content);
    setDraftMessage('');
  };

  const handleFileUpload = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      await uploadFile?.(file);
    }
    event.target.value = '';
  };

  return (
    <PdfContext.Provider value={pdfManager}>
      <AnimatePresence>
        <LayoutGroup>
          <VStack className="size-full max-w-full" gap={0}>
            {currentThreadId === null && (
              <motion.div
                initial={{ x: -64, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: 64, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="flex size-full w-full max-w-full flex-1 flex-col"
              >
                <ChatHeader
                  isLoading={isLoading}
                  showHistoryButton={false}
                  onCreateThread={onCreateThread}
                />

                <VStack className="flex-1 overflow-auto p-4" gap={4}>
                  {threads.map((thread) => (
                    <ChatThreadCard
                      key={thread.id}
                      thread={thread}
                      onSelectThread={onSelectThread}
                      onThreadTitleChange={onThreadTitleUpdate}
                    />
                  ))}
                </VStack>
              </motion.div>
            )}
            {currentThreadId !== null && (
              <motion.div
                initial={{ x: 64, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: -64, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="flex size-full w-full max-w-full flex-1 flex-col"
              >
                <ChatHeader
                  isLoading={isLoading}
                  showHistoryButton={true}
                  onCreateThread={onCreateThread}
                  onSelectThread={onSelectThread}
                  threadId={currentThreadId}
                  threadTitle={currentThread?.title}
                  onThreadTitleChange={onThreadTitleUpdate}
                />
                <VStack
                  className={cn(
                    'flex-1 overflow-y-auto p-4',
                    !hasMessages && 'items-center justify-center p-16 pt-0',
                  )}
                  gap={hasMessages ? 4 : 8}
                  ref={messageListRef}
                >
                  {hasMessages ? (
                    <>
                      {currentThread.messages.map((message, idx) => {
                        const isLastMessage =
                          idx === currentThread.messages.length - 1;
                        const isLastMessageStreaming =
                          isStreaming && isLastMessage;

                        return (
                          <VStack key={message.id}>
                            <ChatMessageBubble
                              message={message}
                              isStreaming={isLastMessageStreaming}
                              templateMessages={startingMessages}
                              onCopy={() => {
                                analytics.capture('chat_message_copied', {
                                  tenantId,
                                  userId,
                                  patientId,
                                  threadId: currentThreadId,
                                  messageId: message.id,
                                  messageRole: message.role,
                                  messageContent: message.content,
                                  wordCount: message.content
                                    ?.trim()
                                    ?.split(/\s+/)?.length,
                                });
                              }}
                            />
                            {!isStreaming && message.role === 'assistant' && (
                              <HStack className="pl-4">
                                <ChatMessageFeedback messageId={message.id} />
                              </HStack>
                            )}
                            {!isLastMessageStreaming &&
                            message.citations?.length ? (
                              <VStack className="pl-4">
                                <Divider className="my-6 border-border-primary" />
                                <CitationsSection
                                  citations={message.citations}
                                />
                              </VStack>
                            ) : null}
                          </VStack>
                        );
                      })}
                      {isPending && (
                        <div className="w-full self-start">
                          <VStack justify="center" className=" space-x-1 p-4">
                            <div className="size-3 animate-pulse rounded-full bg-text-tertiary"></div>
                          </VStack>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      {(startingMessages ?? []).length === 0 ? (
                        <Text
                          size="md"
                          weight="medium"
                          color="tertiary"
                          className="text-center"
                        >
                          You can ask about the patient's data,
                          <br />
                          or any other medical questions.
                        </Text>
                      ) : (
                        <Heading
                          weight="semibold"
                          size="lg"
                          color="tertiary"
                          className="text-center"
                        >
                          Get started with Eluve Insights
                        </Heading>
                      )}
                      {startingMessages?.map((message) => (
                        <StartingChatMessageCard
                          key={message.id}
                          message={message}
                          onSelectMessage={handleStartingMessageSelect}
                        />
                      ))}
                      <VStack gap={1} align="center">
                        <Text size="sm" color="tertiary">
                          AI can make mistakes. Please double-check responses.
                        </Text>
                        <Text size="sm" color="tertiary">
                          See{' '}
                          <Link
                            target="_blank"
                            to="https://www.eluve.com/terms"
                          >
                            T&C
                          </Link>{' '}
                          for more info.
                        </Text>
                      </VStack>
                    </>
                  )}
                </VStack>
                <VStack className="border-t border-border-primary p-3">
                  {pdfManager.loading ? (
                    <Icon
                      name="LoaderCircle"
                      size="xs"
                      className="mx-3 h-[44px] animate-spin py-2"
                    />
                  ) : (
                    pdfManager.uploadedFiles.length > 0 && (
                      <HStack gap={2} align="start" wrap>
                        {pdfManager.uploadedFiles.map((file: UploadedFile) => (
                          <TooltipLabel
                            label={
                              file.hasPassword
                                ? 'This PDF is password protected. Click to unlock it!'
                                : 'Preview this PDF'
                            }
                            key={file.id}
                          >
                            <div
                              className="cursor-pointer border border-border-primary hover:bg-bg-secondary"
                              onClick={() => {
                                if (file.hasPassword) {
                                  setPasswordModal({
                                    isOpen: true,
                                    fileId: file.id,
                                    fileName: file.fileName || null,
                                  });
                                } else {
                                  pdfManager.handlePreview(file);
                                }
                              }}
                            >
                              <HStack gap={2} align="center" className="h-full">
                                <div className="ml-2">
                                  {file.hasPassword ? (
                                    <Icon
                                      name="AlertCircle"
                                      size="sm"
                                      className="cursor-pointer text-bg-negative-strong-primary"
                                    />
                                  ) : (
                                    <Icon name="FileText" size="sm" />
                                  )}
                                </div>
                                <Text
                                  size="sm"
                                  weight="medium"
                                  color="primary"
                                  className="truncate"
                                >
                                  {file.fileName}
                                </Text>
                                <NewButton
                                  icon={{ name: 'X' }}
                                  type="ghost"
                                  size="m"
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    await handleFileRemove(file.id);
                                  }}
                                />
                              </HStack>
                            </div>
                          </TooltipLabel>
                        ))}
                      </HStack>
                    )
                  )}
                  <HStack className="border-border-primary pt-3" gap={4}>
                    {currentThreadId && (
                      <>
                        <Input
                          ref={fileInputRef}
                          type="file"
                          accept=".pdf"
                          onChange={handleFileChange}
                          disabled={isUploading}
                          className="hidden"
                        />
                        <NewButton
                          loading={isUploading}
                          icon={{ name: 'Paperclip' }}
                          type="outline"
                          size="m"
                          onClick={handleFileUpload}
                          disabled={isLoading || isUploading}
                        />
                      </>
                    )}
                    <Textarea
                      compact
                      disabled={isStreaming}
                      onChange={handleDraftMessageChange}
                      onKeyDown={handleDraftMessageKeyDown}
                      onSubmit={handleSendMessageClick}
                      placeholder="Ask anything"
                      ref={textAreaRef}
                      value={draftMessage}
                    />
                    <NewButton
                      disabled={isStreaming}
                      icon={{ name: 'ArrowUp' }}
                      onClick={handleSendMessageClick}
                    />
                  </HStack>
                </VStack>
              </motion.div>
            )}
          </VStack>
          <PdfPreviewDialog />
          <PdfPasswordModal
            isOpen={passwordModal.isOpen}
            fileId={passwordModal.fileId}
            fileName={passwordModal.fileName}
            tenantId={tenantId}
            threadId={currentThreadId ?? ''}
            onClose={() =>
              setPasswordModal({ isOpen: false, fileId: null, fileName: null })
            }
            onDecryptSuccess={() => {
              if (currentThreadId) {
                pdfManager?.loadThreadFiles(currentThreadId);
              }
            }}
          />
        </LayoutGroup>
      </AnimatePresence>
    </PdfContext.Provider>
  );
};
